import { useAppDispatch, useAppSelector } from "../../stores/hooks/hooks.ts";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createBrand,
  editBrandById,
  fetchBrandList,
} from "../../stores/thunks/brandThunk.tsx";
import { FieldErrors, FormProvider, useForm } from "react-hook-form";
import { faArrowLeft } from "@fortawesome/pro-light-svg-icons";
import { brandTabsData } from "./brandDetailsData.ts";
import { DetailsView } from "./components/views/details/DetailsView.tsx";
import { BrandGuidelinesView } from "./components/views/guidelines/BrandGuidelinesView.tsx";
import {
  BrandEditByIdBodyType,
  BrandItemType,
} from "../../stores/types/brandTypes.tsx";
import {
  RevActionsHeader,
  RevButton,
  RevEmptyPage,
  RevIconButton,
  RevTabs,
  RevUnsavedChangesDialog,
} from "@revmatics/catalyst";
import {
  hasValidDomain,
  isValidUrl,
} from "../../helpers/functions/hasValidHelpers.ts";
import { switchActiveTab } from "../../helpers/functions/switchActiveTab.tsx";
import { useUnsavedChangesDialog } from "../../helpers/hooks/useUnsavedChangesDialog.tsx";
import { useFlags } from "launchdarkly-react-client-sdk";
import { NotFoundPage } from "../notFound/NotFoundPage.tsx";
import { SocialHandlesView } from "./components/views/socialHandles/SocialHandlesView.tsx";
import {
  CompetitorCreateDataType,
  CompetitorEditDataType,
  CompetitorLocalItemType,
  PlatformsUsernameState,
  UsernameCreateDataType,
  UsernameEditDataType,
  UsernameItem,
} from "../../stores/types/brandSocialHandlesTypes.tsx";
import {
  createCompetitor,
  createCreatorPartnerUsernames,
  createSocialUsernames,
  deleteCompetitorById,
  deleteCreatorPartnerUsernamesById,
  deleteSocialUsernamesById,
  editCompetitorById,
  editCreatorPartnerUsernamesById,
  editSocialUsernamesById,
} from "../../stores/thunks/brandSocialHandlesThunk.tsx";
import campaignEmpty from "/images/empty_states/campaign-empty.svg";

type Props = {
  brandById: BrandItemType | null;
};

export const BrandDetailsPage = ({ brandById }: Props) => {
  const { addBrand: ffAddBrand, brandDetails: ffBrandDetails } = useFlags();
  const dispatch = useAppDispatch();
  const { companyId } = useParams();
  const navigate = useNavigate();
  const isLoadingEditById = useAppSelector(
    (state) => state.brand.isLoadingEditById,
  );
  const {
    usernames,
    competitors,
    partnerUsernames,
    usernamesToDelete,
    competitorsToDelete,
    partnerUsernamesToDelete,
  } = useAppSelector((state) => state.brandSocialHandles);

  const [activeTab, setActiveTab] = useState("details");
  const [href, setHref] = useState("");
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const isLoading = isLoadingEditById || isLoadingSubmit;
  const [isOpenUnsavedModal, setOpenUnsavedModal] = useState(false);
  const organizationsList = useAppSelector(
    (state) => state.organizations.organizationsList,
  );
  const companySettings = useAppSelector(
    (state) => state.company.companySettings,
  );
  const { brandId } = useParams();
  const [searchParams] = useSearchParams();

  const methods = useForm<BrandEditByIdBodyType>({
    mode: "onSubmit",
    defaultValues: {
      name: brandById?.name || "",
      website: brandById?.website || "",
      annualized_revenue: brandById?.annualized_revenue || "",
      dos: brandById?.dos || "",
      donts: brandById?.donts || "",
      brand_safety: brandById?.brand_safety || "",
    },
  });
  const {
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = methods;

  useEffect(() => {
    if (brandById) {
      reset({
        name: brandById?.name || "",
        website: brandById?.website || "",
        annualized_revenue: brandById?.annualized_revenue || "",
        dos: brandById?.dos || "",
        donts: brandById?.donts || "",
        brand_safety: brandById?.brand_safety || "",
      });
    }
  }, [brandById, reset]);

  const dirtyFieldsWithoutUsernames = Object.keys(dirtyFields).length > 0;

  // Navigates to the tab specified in a query parameter
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    switchActiveTab(tabFromUrl, brandTabsData, setActiveTab);
  }, [searchParams]);

  const getUsernamesItemsFromData = useCallback(
    (data: PlatformsUsernameState | null) => {
      const result = {
        edit: [] as Array<UsernameEditDataType>,
        create: [] as Array<UsernameCreateDataType>,
      };
      if (!data) return result;

      Object.keys(data).forEach((key) => {
        const platformId = Number(key);
        const list: UsernameItem[] = data[platformId];
        list.forEach((item) => {
          if (item.status !== "valid") return;

          if (item.mode === "edited") {
            result.edit.push({
              usernameId: item.id,
              brandId: Number(brandId),
              body: {
                platform_id: Number(platformId),
                username: item.username,
              },
            });
          }

          if (item.mode === "new") {
            result.create.push({
              brandId: Number(brandId),
              body: {
                platform_id: Number(platformId),
                username: item.username,
              },
            });
          }
        });
      });
      return result;
    },
    [brandId],
  );

  const listForRequestUsernames = useMemo(() => {
    return getUsernamesItemsFromData(usernames);
  }, [getUsernamesItemsFromData, usernames]);

  const listForRequestCompetitor = useMemo(() => {
    const result = {
      edit: [] as CompetitorEditDataType[],
      create: [] as CompetitorCreateDataType[],
    };

    if (!competitors) return result;

    competitors.forEach((item) => {
      const socialUsernames = Object.entries(item.social_usernames).flatMap(
        ([platformId, usernames]) =>
          usernames
            .filter((username) => username.status === "valid")
            .map((username) => ({
              platform_id: Number(platformId),
              username: username.username,
            })),
      );
      if (socialUsernames.length === 0) return;
      const body = {
        website: item.website,
        strength: item.strength,
        social_usernames: socialUsernames,
      };

      if (item.mode === "new") {
        result.create.push({
          body,
          brandId: Number(brandId),
        });
      }

      if (item.mode === "edited") {
        result.edit.push({
          body,
          brandId: Number(brandId),
          competitorId: item.id,
        });
      }
    });

    return result;
  }, [brandId, competitors]);

  const listForRequestPartnerUsernames = useMemo(() => {
    return getUsernamesItemsFromData(partnerUsernames);
  }, [getUsernamesItemsFromData, partnerUsernames]);

  const hasFailedUsernames = useMemo(() => {
    const check = (data: PlatformsUsernameState | null) =>
      data
        ? Object.values(data).some((list) =>
            list.some((item) => item.status === "failed"),
          )
        : false;

    const checkCompetitor = (data: CompetitorLocalItemType[] | null): boolean =>
      Boolean(
        data?.some(({ website, social_usernames }) => {
          const isFailedUsername = Object.values(social_usernames).some(
            (usernames) => usernames.some((item) => item.status === "failed"),
          );
          const isValidUsername = Object.values(social_usernames).some(
            (usernames) => usernames.some((item) => item.status === "valid"),
          );
          return (isValidUsername && !website) || isFailedUsername;
        }),
      );

    return (
      check(usernames) ||
      checkCompetitor(competitors) ||
      check(partnerUsernames)
    );
  }, [usernames, partnerUsernames, competitors]);

  const hasUsernameChanges =
    listForRequestUsernames.edit.length > 0 ||
    listForRequestUsernames.create.length > 0 ||
    listForRequestPartnerUsernames.edit.length > 0 ||
    listForRequestPartnerUsernames.create.length > 0 ||
    listForRequestCompetitor.edit.length > 0 ||
    listForRequestCompetitor.create.length > 0 ||
    usernamesToDelete.length > 0 ||
    competitorsToDelete.length > 0 ||
    partnerUsernamesToDelete.length > 0;

  const hasUnsavedChanges = dirtyFieldsWithoutUsernames || hasUsernameChanges;

  useUnsavedChangesDialog({
    hasUnsavedChanges,
    onNavigateAttempt: (href) => {
      setHref(href);
      setOpenUnsavedModal(true);
    },
  });

  const onError = (errors: FieldErrors<BrandEditByIdBodyType>) => {
    if (errors.name) {
      setActiveTab("details");
    }
  };

  const handleDiscardChanges = () => {
    setOpenUnsavedModal(false);

    // If we have a stored destination from the hook, go there.
    // Otherwise, default to the previous page (navigate -1).
    if (href) {
      navigate(href);
    } else {
      navigate(-1);
    }
    setHref("");
  };

  const onSubmit = (data: BrandEditByIdBodyType) => {
    setIsLoadingSubmit(true);

    if (listForRequestUsernames.create.length > 0) {
      listForRequestUsernames.create.forEach((item) =>
        dispatch(createSocialUsernames(item)),
      );
    }
    if (listForRequestUsernames.edit.length > 0) {
      listForRequestUsernames.edit.forEach((item) =>
        dispatch(editSocialUsernamesById(item)),
      );
    }
    if (listForRequestCompetitor.create.length > 0) {
      listForRequestCompetitor.create.forEach((item) =>
        dispatch(createCompetitor(item)),
      );
    }
    if (listForRequestCompetitor.edit.length > 0) {
      listForRequestCompetitor.edit.forEach((item) =>
        dispatch(editCompetitorById(item)),
      );
    }
    if (listForRequestPartnerUsernames.create.length > 0) {
      listForRequestPartnerUsernames.create.forEach((item) =>
        dispatch(createCreatorPartnerUsernames(item)),
      );
    }
    if (listForRequestPartnerUsernames.edit.length > 0) {
      listForRequestPartnerUsernames.edit.forEach((item) =>
        dispatch(editCreatorPartnerUsernamesById(item)),
      );
    }
    if (usernamesToDelete.length > 0) {
      usernamesToDelete.forEach((item) =>
        dispatch(
          deleteSocialUsernamesById({
            usernameId: item,
            brandId: Number(brandId),
          }),
        ),
      );
    }
    if (competitorsToDelete.length > 0) {
      competitorsToDelete.forEach((item) =>
        dispatch(
          deleteCompetitorById({
            competitorId: item,
            brandId: Number(brandId),
          }),
        ),
      );
    }
    if (partnerUsernamesToDelete.length > 0) {
      partnerUsernamesToDelete.forEach((item) =>
        dispatch(
          deleteCreatorPartnerUsernamesById({
            usernameId: item,
            brandId: Number(brandId),
          }),
        ),
      );
    }

    if (!dirtyFieldsWithoutUsernames) {
      dispatch(fetchBrandList());
      if (href) {
        navigate(href);
        return;
      }
      if (hasUsernameChanges) {
        navigate(-1);
      }
      setIsLoadingSubmit(false);
    }

    const formDataForApi = new FormData();
    if (companyId) {
      formDataForApi.append("company_id", companyId);
    }
    if (brandById && dirtyFieldsWithoutUsernames) {
      Object.keys(data).forEach((item) => {
        const key = item as keyof typeof data;
        if (brandById[key] !== data[key]) {
          formDataForApi.append(key, data[key] || "");
        }
      });
      dispatch(
        editBrandById({
          id: brandById.id.toString(),
          body: formDataForApi,
          callback: () => {
            dispatch(fetchBrandList());
            if (href) {
              navigate(href);
              return;
            }
            if (hasUnsavedChanges) {
              navigate(-1);
            }
            setIsLoadingSubmit(false);
          },
        }),
      );
    } else if (!brandId) {
      Object.keys(data).forEach((item) => {
        const key = item as keyof typeof data;
        if (
          key === "website" &&
          data[key] &&
          hasValidDomain(data[key]) &&
          !isValidUrl(data[key])
        ) {
          formDataForApi.append(key, "https://" + data[key]);
        } else {
          formDataForApi.append(key, data[key] || "");
        }
      });
      dispatch(
        createBrand({
          callback: () => {
            dispatch(fetchBrandList());
            navigate("/company-settings");
            setIsLoadingSubmit(false);
          },
          body: formDataForApi,
        }),
      );
    }
  };

  const isAdmin = useMemo(() => {
    if (!companySettings) return false;
    return (
      organizationsList?.find((org) => org.id === companySettings.id)?.role ===
      "ORGANIZATION_ADMIN"
    );
  }, [companySettings, organizationsList]);

  if (brandId ? ffBrandDetails : ffAddBrand && isAdmin) {
    return (
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit, onError)}
          className="max-w-[754px] mx-auto space-y-6"
          noValidate
        >
          <RevUnsavedChangesDialog
            pageId="profile-settings-page"
            isOpen={isOpenUnsavedModal}
            setIsOpen={setOpenUnsavedModal}
            onSave={handleSubmit(onSubmit, onError)}
            onDiscard={handleDiscardChanges}
            isLoading={isLoading}
          />
          <RevActionsHeader
            title={brandById?.name || "Create New Brand"}
            headerStick
            backButton={
              <RevIconButton
                handleClick={() => navigate(-1)}
                icon={faArrowLeft}
                size="xl"
                variant="outline"
                iconSize="medium"
                aria-label="Back button brand details"
                className="bg-white"
              />
            }
          >
            {isAdmin && (
              <RevButton
                text="Save"
                type="submit"
                buttonSize="medium"
                isLoading={isLoading}
                disabled={!hasUnsavedChanges || hasFailedUsernames}
              />
            )}
          </RevActionsHeader>
          {brandById && (
            <RevTabs
              size="medium"
              data={brandTabsData}
              tabId={`brand-tab-${brandId}`}
              selected={activeTab}
              handleSelect={(value: string) => setActiveTab(value)}
            />
          )}
          <div className={activeTab === "details" ? "block" : "hidden"}>
            <DetailsView readAccessOnly={!isAdmin} />
          </div>
          <div
            className={activeTab === "brand_guidelines" ? "block" : "hidden"}
          >
            <BrandGuidelinesView
              initialTab={searchParams.get("subtab")}
              readAccessOnly={!isAdmin}
            />
          </div>
          <div
            className={
              activeTab === "social_handles" && brandId ? "block" : "hidden"
            }
          >
            <SocialHandlesView />
          </div>
        </form>
      </FormProvider>
    );
  }

  if (!brandId && (!ffAddBrand || !isAdmin)) {
    return (
      <RevEmptyPage
        title="Add Brand"
        image={campaignEmpty}
        text="In order to add another brand, please contact us so that a Revmatics specialist can help you."
        textBtn="Contact Us"
        to="/contact-us"
      />
    );
  }
  return <NotFoundPage />;
};
